import { Map as ImmutableMap, type RecordOf } from 'immutable';
import { type ActionMeta, createAction as reduxActionsCreator } from 'redux-actions';

import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import type { Biller, EnrollResponse } from 'app-components/flux/bill-presentment/billPresentmentTypes';
import type { scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import { tracker } from 'app-components/utils/core';
import { ROOT_MODAL_DRAWER_PROP, ROOT_MODAL_PRESENTATION_PROP } from 'components/Drawers/standardDrawerContract';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

export const DIALOG_TYPE = 'BILL_PRESENTMENT_DIALOG';
export const DETAILS_DIALOG_TYPE = 'BILL_PRESENTMENT_DETAILS_DIALOG';

export interface BillPresentmentProps {
  series: scheduledTransactionsTypes.ScheduledTransactionType;
  initiator?: string;
  rootModalDrawer?: RootModalDrawerOptions;
}

export interface BillPresentmentDetailsProps {
  biller: Biller;
  enrollment: EnrollResponse;
  guid: string;
  rootModalDrawer?: RootModalDrawerOptions;
  series: scheduledTransactionsTypes.ScheduledTransactionType;
}

// Dialog Actions
// redux-actions passes every argument to both creators; its overloads drop the optional ones.
export const doBillPresentment: (
  series: scheduledTransactionsTypes.ScheduledTransactionType,
  initiator?: string,
  rootModalDrawer?: RootModalDrawerOptions,
) => ActionMeta<RecordOf<rootUiTypes.RootUiData>, string | undefined> = reduxActionsCreator(
  rootUiActions.createDialog.toString(),
  (
    series: scheduledTransactionsTypes.ScheduledTransactionType,
    initiator?: string,
    rootModalDrawer?: RootModalDrawerOptions,
  ) => {
    const reconnect = Boolean(series?.presentmentAccountId);
    const normalizedRootModalDrawer = rootModalDrawer ? { ...rootModalDrawer, width: 'Large' as const } : undefined;
    const presentationProps = rootModalDrawer
      ? { [ROOT_MODAL_DRAWER_PROP]: normalizedRootModalDrawer }
      : { [ROOT_MODAL_PRESENTATION_PROP]: 'modal' };

    tracker.track(tracker.events.addBillStart, { initiator: location, flow: reconnect ? 'reconnect' : 'addNew' });

    return rootUiTypes.mkRootUiData({
      type: DIALOG_TYPE,
      id: `BPS-${series?.id}`,
      props: ImmutableMap({
        ...presentationProps,
        series,
        initiator,
      }),
    });
  },
  (_series: scheduledTransactionsTypes.ScheduledTransactionType, initiator?: string) => initiator,
);

export const showBillPresentmentDetails = reduxActionsCreator(
  rootUiActions.createDialog.toString(),
  ({ series, ...props }: BillPresentmentDetailsProps) =>
    rootUiTypes.mkRootUiData({
      type: DETAILS_DIALOG_TYPE,
      id: `${DETAILS_DIALOG_TYPE}-${series.id}`,
      props: ImmutableMap({
        ...props,
        series,
      }),
    }),
);
